export type RideableWaveInput = {
  swellWaveHeightM: number;
  swellWavePeriodS: number;
  swellWaveDirectionDeg: number | null;
  windWaveHeightM: number;
  windWavePeriodS?: number | null;
  windWaveDirectionDeg?: number | null;
};

/** A surfer can ride a local wind sea as well as a travelling swell. Evaluate
 * it with the same wave response; never add a combined sea to its components.
 * The other wave train remains surface interference, rather than letting a
 * partition count as both the ridden wave and its own chop. */
export function windSeaAlternative<T extends RideableWaveInput>(marine: T): T | null {
  if (!(marine.windWaveHeightM > 0) ||
      marine.windWavePeriodS == null || !(marine.windWavePeriodS > 0)) return null;
  return {
    ...marine,
    swellWaveHeightM: marine.windWaveHeightM,
    swellWavePeriodS: marine.windWavePeriodS,
    swellWaveDirectionDeg: marine.windWaveDirectionDeg ?? null,
    windWaveHeightM: marine.swellWaveHeightM,
    windWavePeriodS: marine.swellWavePeriodS,
    windWaveDirectionDeg: marine.swellWaveDirectionDeg,
  };
}

/** Recover only the missing primary variance. Combined height already contains
 * wind sea and secondary swell, so it cannot itself be another partition. */
export function recoverPrimaryWaveHeight(
  primary: number | null | undefined,
  combined: number | null | undefined,
  wind: number | null | undefined,
  secondary: number | null | undefined,
): number | null {
  const valid = (value: number | null | undefined) =>
    typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
  const measured = valid(primary);
  if (measured !== null) return measured;
  const total = valid(combined);
  if (total === null) return null;
  return Math.sqrt(Math.max(0, total ** 2 - (valid(wind) ?? 0) ** 2 - (valid(secondary) ?? 0) ** 2));
}
